import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, Output, ViewChild } from '@angular/core';
import {
  ECElementEvent,
  ECharts,
  EChartsOption,
  GridComponentOption,
  LegendComponentOption,
  ScatterSeriesOption,
  Payload,
  TitleComponentOption,
  TooltipComponentOption,
  XAXisComponentOption,
  YAXisComponentOption,
  ToolboxComponentOption
} from 'echarts';
import { NgxEchartsDirective, ThemeOption } from 'ngx-echarts';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-double-axis-scatter-echart',
  templateUrl: './double-axis-scatter-echart.component.html',
  styleUrl: './double-axis-scatter-echart.component.css'
})
export class DoubleAxisScatterEchartComponent {

  private _chartInstance: ECharts;

  @ViewChild(NgxEchartsDirective) plotRef: NgxEchartsDirective;

  @Input()
  set title(value: TitleComponentOption) {
    (<TitleComponentOption>this.options.title) = { ...value };
    this._plot();
  }

  @Input()
  set legend(value: LegendComponentOption) {
    (<LegendComponentOption>this.options.legend) = { ...value };
    this._plot();
  }

  @Input()
  set series(list: ScatterSeriesOption[]) {
    (<ScatterSeriesOption[]>this.options.series) = list?.map(
      (data) => {
        return {
          ...data,
          type: 'scatter'
        }
      });
    this._plot();
  }

  @Input()
  set tooltip(value: TooltipComponentOption) {
    (<TooltipComponentOption>this.options.tooltip) = { ...value };
    this._plot();
  }

  @Input()
  set xAxis(value: XAXisComponentOption) {
    (<XAXisComponentOption>this.options.xAxis) = { ...value };
    this._plot();
  }

  @Input()
  set yAxis(value: YAXisComponentOption[]) {
    (<YAXisComponentOption[]>this.options.yAxis) = value;
    this._plot();
  }

  @Input()
  set grid(value: GridComponentOption) {
    (<GridComponentOption>this.options.grid) = { ...value };
    this._plot();
  }

  @Input()
  set toolbox(value: ToolboxComponentOption) {
    (<Record<string, any>>this.options.toolbox) = { ...value };
    this._plot();
  }

  @Input() merge: EChartsOption;
  @Input() autoResize: boolean;
  @Input() theme: string | ThemeOption;
  @Input() loadingOpts: object = null;

  @Input() initOpts: {
    devicePixelRatio?: number;
    renderer?: string;
    width?: number | string;
    height?: number | string;
    locale?: string;
  } = {
    width: 'auto',
    height: 'auto'
  };

  @Input() 
  set dispatchAction(payload: Payload) {
    this._chartRenderedSubscription = this.chartRendered
    .subscribe({
      next: () => {
        this._chartInstance?.dispatchAction(payload);
      },
      error: (errorResponse: HttpErrorResponse) => { }
    });
  }

  // Outputs
  @Output() chartInit = new Subject<ECharts>();
  @Output() chartClick = new Subject<ECElementEvent>();
  @Output() chartDblClick = new Subject<ECElementEvent>();
  @Output() chartMouseDown = new Subject<ECElementEvent>();
  @Output() chartMouseMove = new Subject<ECElementEvent>();
  @Output() chartMouseUp = new Subject<ECElementEvent>();
  @Output() chartMouseOver = new Subject<ECElementEvent>();
  @Output() chartMouseOut = new Subject<ECElementEvent>();
  @Output() chartRendered = new Subject<boolean>();
  @Output() chartFinished = new Subject<boolean>();
  @Output() dataZoom = new Subject<ECElementEvent>();

  options: EChartsOption = {
    title: {} as TitleComponentOption,
    legend: {} as LegendComponentOption,
    series: [] as ScatterSeriesOption[],
    tooltip: {} as TooltipComponentOption,
    xAxis: {} as XAXisComponentOption,
    yAxis: [] as YAXisComponentOption[],
    grid: {} as GridComponentOption
  };

  loading: boolean = false;
  private _chartRenderedSubscription: Subscription;

  constructor() { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this._chartRenderedSubscription?.unsubscribe();
  }

  onChartInit(value: ECharts) {
    this._chartInstance = value;
    this.chartInit.next(this._chartInstance);
  }

  onChartClick(value: unknown) {
    this.chartClick.next(value as ECElementEvent);
  }

  onChartDblClick(value: unknown) {
    this.chartDblClick.next(value as ECElementEvent);
  }

  onChartMouseDown(value: unknown) {
    this.chartMouseDown.next(value as ECElementEvent);
  }

  onChartMouseMove(value: unknown) {
    this.chartMouseMove.next(value as ECElementEvent);
  }

  onChartMouseUp(value: unknown) {
    this.chartMouseUp.next(value as ECElementEvent);
  }

  onChartMouseOver(value: unknown) {
    this.chartMouseOver.next(value as ECElementEvent);
  }

  onChartMouseOut(value: unknown) {
    this.chartMouseOut.next(value as ECElementEvent);
  }

  onChartRendered() {
    this.chartRendered.next(true);
    this._setLoader(false);
  }

  onChartFinished() {
    this.chartFinished.next(true);
  }

  private _plot() {
    this.plotRef?.refreshChart();
    this._setLoader(true);
  }

  private _setLoader(option: boolean) {
    this.loading = option;
  }

    /**
   * Determines whether data zoom on
   * @param value 
   */
     onDataZoom(value: unknown) {
      this.dataZoom.next(value as ECElementEvent);
    }
}
